/**
 * Admin Beneficiaries Management Page
 * 
 * Allows administrators to approve/reject pending beneficiaries
 * and process removal requests
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import apiClient from '@/utils/api';
import { toast } from 'sonner';
import AdminLayout from '@/components/AdminLayout';
import { 
  CheckCircle, 
  XCircle,
  Clock,
  Trash2,
  AlertTriangle,
  User,
  Calendar,
  Heart,
  RefreshCw
} from 'lucide-react';

// Helper function to calculate age
const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return 'N/A';
  const dob = new Date(dateOfBirth);
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }
  return `${Math.max(0, age)} years`;
};

// RequestCard component (moved outside to avoid nested component issue)
const RequestCard = ({ request, onOpenActionDialog }) => {
  const { member_name, member_email, member_id, beneficiary, request_type } = request;
  const isRemoval = request_type === 'removal';
  
  return (
    <Card 
      className={`${isRemoval ? 'border-red-200 bg-red-50/50' : 'border-amber-200 bg-amber-50/50'}`}
      data-testid={`beneficiary-request-${beneficiary.beneficiary_id}`}
    >
      <CardContent className="pt-6">
        <div className="flex flex-col lg:flex-row justify-between gap-4">
          <div className="flex-1">
            {/* Member Info */}
            <div className="mb-4">
              <p className="text-sm text-muted-foreground">Member</p>
              <p className="font-medium">{member_name}</p>
              <p className="text-sm text-muted-foreground">{member_email}</p>
              <p className="text-xs text-muted-foreground font-mono">{member_id}</p>
            </div>
            
            {/* Beneficiary Info */}
            <div className="border-t pt-4">
              <div className="flex items-center gap-2 mb-2">
                <Badge className={isRemoval ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}>
                  {isRemoval ? 'Removal Requested' : 'Pending Approval'}
                </Badge>
                {beneficiary.is_primary && (
                  <Badge variant="outline">Primary</Badge>
                )}
              </div>
              
              <h4 className="font-medium text-lg mb-2">{beneficiary.full_name}</h4>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="flex items-center gap-2">
                  <Heart className="w-4 h-4 text-muted-foreground" />
                  <span>{beneficiary.relationship}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span>{beneficiary.date_of_birth || 'N/A'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <span>Age: {calculateAge(beneficiary.date_of_birth)}</span>
                </div>
                {beneficiary.added_at && (
                  <div className="text-muted-foreground">
                    Added: {new Date(beneficiary.added_at).toLocaleDateString()}
                  </div>
                )}
              </div>
              
              {isRemoval && beneficiary.removal_reason && (
                <div className="mt-3 p-3 bg-red-100 rounded-lg">
                  <p className="text-sm font-medium text-red-800">Removal Reason:</p>
                  <p className="text-sm text-red-700">{beneficiary.removal_reason}</p>
                </div>
              )}
              
              {beneficiary.notes && !isRemoval && (
                <p className="text-sm text-muted-foreground mt-2 italic">
                  Note: {beneficiary.notes}
                </p>
              )}
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex lg:flex-col gap-2 justify-end">
            {isRemoval ? (
              <>
                <Button 
                  onClick={() => onOpenActionDialog(request, 'approve_removal')}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid={`approve-removal-${beneficiary.beneficiary_id}`}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve Removal
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => onOpenActionDialog(request, 'reject_removal')}
                  data-testid={`reject-removal-${beneficiary.beneficiary_id}`}
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Deny Removal
                </Button>
              </>
            ) : (
              <>
                <Button 
                  onClick={() => onOpenActionDialog(request, 'approve')}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid={`approve-beneficiary-${beneficiary.beneficiary_id}`}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve
                </Button>
                <Button 
                  variant="outline"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => onOpenActionDialog(request, 'reject')}
                  data-testid={`reject-beneficiary-${beneficiary.beneficiary_id}`}
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const AdminBeneficiaries = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total_pending: 0, total_removal: 0 });
  const [activeTab, setActiveTab] = useState('pending');
  
  // Dialog states
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionType, setActionType] = useState(null); // 'approve', 'reject', 'approve_removal', 'reject_removal'
  const [notes, setNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadPendingRequests();
  }, []);

  const loadPendingRequests = async () => {
    try {
      const response = await apiClient.get('/members/admin/pending-requests');
      setRequests(response.data.requests || []);
      setStats({
        total_pending: response.data.total_pending || 0,
        total_removal: response.data.total_removal || 0
      });
    } catch (error) {
      toast.error('Failed to load beneficiary requests');
      console.error('Error loading requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const openActionDialog = (request, action) => {
    setSelectedRequest(request);
    setActionType(action);
    setNotes('');
    setActionDialogOpen(true);
  };

  const handleAction = async () => {
    if (!selectedRequest || !actionType) return;
    
    setProcessing(true);
    try {
      const { member_id, beneficiary } = selectedRequest;
      const beneficiary_id = beneficiary.beneficiary_id;
      
      let endpoint;
      let data = {};
      
      switch (actionType) {
        case 'approve':
          endpoint = `/members/admin/beneficiaries/${member_id}/${beneficiary_id}/approve`;
          if (notes) data.notes = notes;
          break;
        case 'reject':
          endpoint = `/members/admin/beneficiaries/${member_id}/${beneficiary_id}/reject`;
          data.reason = notes || 'Rejected by administrator';
          break;
        case 'approve_removal':
          endpoint = `/members/admin/beneficiaries/${member_id}/${beneficiary_id}/process-removal?approve=true`;
          if (notes) data.notes = notes;
          break;
        case 'reject_removal':
          endpoint = `/members/admin/beneficiaries/${member_id}/${beneficiary_id}/process-removal?approve=false`;
          data.notes = notes || 'Removal request denied';
          break;
        default:
          throw new Error('Invalid action type');
      }

      await apiClient.post(endpoint, data);
      
      const messages = {
        approve: 'Beneficiary approved successfully',
        reject: 'Beneficiary rejected',
        approve_removal: 'Beneficiary removed successfully',
        reject_removal: 'Removal request denied'
      };
      
      toast.success(messages[actionType]);
      setActionDialogOpen(false);
      setSelectedRequest(null);
      setActionType(null);
      setNotes('');
      loadPendingRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process request');
      console.error('Error processing request:', error);
    } finally {
      setProcessing(false);
    }
  };

  const pendingApprovals = requests.filter(r => r.request_type === 'approval');
  const removalRequests = requests.filter(r => r.request_type === 'removal');

  if (loading) {
    return (
      <AdminLayout currentPage="beneficiaries" title="Beneficiary Requests">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout currentPage="beneficiaries" title="Beneficiary Requests">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-lg">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total_pending}</p>
                <p className="text-sm text-muted-foreground">Pending Approvals</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-red-100 rounded-lg">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total_removal}</p>
                <p className="text-sm text-muted-foreground">Removal Requests</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Refresh Button */}
      <div className="flex justify-end mb-4">
        <Button variant="outline" onClick={loadPendingRequests} data-testid="refresh-requests">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="pending" data-testid="tab-pending">
            Pending Approvals
            {stats.total_pending > 0 && (
              <Badge className="ml-2 bg-amber-100 text-amber-800">{stats.total_pending}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="removal" data-testid="tab-removal">
            Removal Requests
            {stats.total_removal > 0 && (
              <Badge className="ml-2 bg-red-100 text-red-800">{stats.total_removal}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
          {pendingApprovals.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">All Caught Up!</h3>
                <p className="text-muted-foreground">No pending beneficiary approvals</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {pendingApprovals.map((request) => (
                <RequestCard 
                  key={`${request.member_id}-${request.beneficiary.beneficiary_id}`} 
                  request={request} 
                  onOpenActionDialog={openActionDialog}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="removal">
          {removalRequests.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">All Caught Up!</h3>
                <p className="text-muted-foreground">No pending removal requests</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {removalRequests.map((request) => (
                <RequestCard 
                  key={`${request.member_id}-${request.beneficiary.beneficiary_id}`} 
                  request={request} 
                  onOpenActionDialog={openActionDialog}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Action Dialog */}
      <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {actionType === 'approve' && <CheckCircle className="w-5 h-5 text-green-600" />}
              {actionType === 'reject' && <XCircle className="w-5 h-5 text-red-600" />}
              {actionType === 'approve_removal' && <Trash2 className="w-5 h-5 text-red-600" />}
              {actionType === 'reject_removal' && <AlertTriangle className="w-5 h-5 text-amber-600" />}
              {actionType === 'approve' && 'Approve Beneficiary'}
              {actionType === 'reject' && 'Reject Beneficiary'}
              {actionType === 'approve_removal' && 'Approve Removal'}
              {actionType === 'reject_removal' && 'Deny Removal Request'}
            </DialogTitle>
            <DialogDescription>
              {selectedRequest && (
                <>
                  <strong>{selectedRequest.beneficiary.full_name}</strong>
                  {' for member '}
                  <strong>{selectedRequest.member_name}</strong>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label htmlFor="action_notes">
              {actionType === 'reject' ? 'Rejection Reason' : 'Notes'} 
              {actionType === 'reject' && <span className="text-muted-foreground"> (optional)</span>}
            </Label>
            <Textarea
              id="action_notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={actionType === 'reject' 
                ? 'Enter reason for rejection...' 
                : 'Add any notes (optional)...'}
              className="mt-2"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialogOpen(false)} disabled={processing}>
              Cancel
            </Button>
            <Button 
              onClick={handleAction}
              disabled={processing}
              className={
                actionType === 'approve' ? 'bg-green-600 hover:bg-green-700' :
                actionType === 'approve_removal' ? 'bg-red-600 hover:bg-red-700' :
                ''
              }
              data-testid="confirm-action-btn"
            >
              {processing ? 'Processing...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminBeneficiaries;
